import type { LoaderFunctionArgs } from "@remix-run/node";
import { json } from "@remix-run/node";
import { useLoaderData, Link } from "@remix-run/react";
import { useQuery } from "convex/react";
import { api } from "#convex/_generated/api.js";
import { HelpDeskHeader } from "#app/components/HelpDeskHeader";
import { useState, useMemo } from "react";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  // Still load tags server-side for initial render
  const { convexHttpClient } = await import("#app/utils/convex.server");
  const tags = await convexHttpClient.query(api.tags.getAllTags);
  
  return json({ tags });
};

export default function Index() {
  const { tags } = useLoaderData<typeof loader>();
  const [searchQuery, setSearchQuery] = useState("");

  // Use live Convex query to get articles with tags
  const articlesWithTags = useQuery(api.helpArticles.getPublishedArticlesWithTags);
  
  // Filter articles client-side based on search query
  // Must call useMemo before any conditional returns to follow Rules of Hooks
  const filteredArticles = useMemo(() => {
    const articles = articlesWithTags || [];
    if (!searchQuery.trim()) {
      return articles;
    }

    const query = searchQuery.toLowerCase().trim();
    
    return articles.filter((article) => {
      // Search in title
      if (article.title.toLowerCase().includes(query)) {
        return true;
      }

      // Search in excerpt
      if (article.excerpt && article.excerpt.toLowerCase().includes(query)) {
        return true;
      }

      // Search in content (strip HTML tags)
      if (article.content) {
        const textContent = article.content.replace(/<[^>]*>/g, "").toLowerCase();
        if (textContent.includes(query)) {
          return true;
        }
      }

      // Search in tag names
      if (article.articleTags && article.articleTags.length > 0) {
        const tagNames = article.articleTags.map((tag) => tag?.name.toLowerCase() || "").join(" ");
        if (tagNames.includes(query)) {
          return true;
        }
      }

      return false;
    });
  }, [articlesWithTags, searchQuery]);
  
  // Show loading state while articles are being fetched
  if (articlesWithTags === undefined) {
    return (
      <div className="min-h-screen bg-offWhite">
        <HelpDeskHeader 
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <p className="text-gray">Loading articles...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-offWhite">
      <HelpDeskHeader 
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {tags.length > 0 && (
          <div className="mb-8">
            <h2 className="text-h5 font-semibold mb-4">Browse by Category</h2>
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <Link
                  key={tag._id}
                  to={`/articles?tag=${tag.slug}`}
                  className="px-4 py-2 bg-white text-blue rounded-lg hover:bg-lightGray transition-colors border border-lightGray"
                  style={tag.color ? { backgroundColor: `${tag.color}20`, color: tag.color, borderColor: tag.color } : {}}
                >
                  {tag.name}
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-4">
          <h2 className="text-h5 font-semibold">
            {searchQuery ? `Search Results (${filteredArticles.length})` : "All Articles"}
          </h2>
          {filteredArticles.length === 0 ? (
            <p className="text-darkGray">
              {searchQuery ? `No articles found for "${searchQuery}"` : "No articles available yet."}
            </p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredArticles.map((article) => (
                <Link
                  key={article._id}
                  to={`/articles/${article.slug}`}
                  className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow border border-lightGray"
                >
                  <h3 className="text-body-md font-semibold text-booBlack mb-2">
                    {article.title}
                  </h3>
                  {article.excerpt && (
                    <p className="text-darkGray text-body-xs mb-4">{article.excerpt}</p>
                  )}
                  {article.articleTags && article.articleTags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {article.articleTags.map((tag) => (
                        tag && (
                          <span
                            key={tag._id}
                            className="px-2 py-0.5 bg-offWhite text-gray rounded text-body-2xs border border-lightGray"
                            style={tag.color ? { backgroundColor: `${tag.color}20`, color: tag.color, borderColor: tag.color } : {}}
                          >
                            {tag.name}
                          </span>
                        )
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between text-body-2xs text-gray">
                    <span>Updated {new Date(article.publishedAt || article.createdAt).toLocaleDateString()}</span>
                    <span>{article.viewCount} views</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
